// Módulos habilitados por empresa (feature flags administrados desde el Panel
// de Propietario). El panel guarda, por cada empresa, qué grupos/ítems del menú
// están encendidos; aquí los leemos por tenant_id y los usamos para ocultarlos
// del sidebar y bloquear la navegación a los módulos apagados.
//
// IMPORTANTE: la lectura va SIEMPRE al proyecto central de Mathiz (donde vive el
// panel), NO al proyecto del cliente. Por eso estas credenciales son fijas y el
// white-label NO las toca: scripts/nueva-marca.mjs solo reescribe
// SUPABASE_URL/SUPABASE_ANON_KEY en core/supabaseClient.js. Es la anon key
// pública; la seguridad la dan (a) que el tenant_id es un UUID no adivinable y
// (b) que la RPC solo expone estos flags de bajo riesgo.
import { NAV_GRUPOS } from "./config.js";

const PANEL_URL = "https://zyfhaoywgxjfqxunirda.supabase.co";
const PANEL_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp5Zmhhb3l3Z3hqZnF4dW5pcmRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODMxMTk0NTEsImV4cCI6MjA5ODY5NTQ1MX0.Lsg2U840--7KF4CoIcUIPy8Z-UZFRpK8TT1O1nNGWaU";

// null = sin restricción (todo activo). Se llena tras leer del panel.
let _flags = null;

// Mapa item.id → grupo.id, derivado de la navegación real del ERP.
const _grupoDeItem = {};
for (const g of NAV_GRUPOS) for (const it of g.items) _grupoDeItem[it.id] = g.id;

// Cliente aparte hacia el proyecto central (sin persistir sesión para no tocar
// la sesión del ERP). Se crea perezosamente cuando ya cargó window.supabase.
let _panelClient = null;
function panelClient() {
  if (!_panelClient) {
    _panelClient = window.supabase.createClient(PANEL_URL, PANEL_ANON_KEY, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
  }
  return _panelClient;
}

// Lee del panel qué módulos tiene habilitados esta empresa. Si algo falla o no
// hay config, deja _flags en null (todo activo) para NO bloquear el ERP.
export async function cargarModulosHabilitados(tenantId) {
  _flags = null;
  if (!tenantId) return;
  try {
    const { data, error } = await panelClient().rpc("modulos_por_tenant", { p_tenant_id: tenantId });
    if (error) { console.warn("[modulos] no se pudieron leer los flags:", error.message); return; }
    if (data && typeof data === "object" && Object.keys(data).length) _flags = data;
  } catch (e) {
    console.warn("[modulos] error leyendo flags:", e && e.message);
  }
}

// ¿Está habilitado un módulo/página para esta empresa? Sin config → true
// (comportamiento completo, sin regresión). Un grupo apagado apaga sus ítems.
export function moduloHabilitado(pageId) {
  if (!_flags) return true;
  if (pageId === "dashboard") return true;
  const grupo = _grupoDeItem[pageId];
  if (!grupo) return true;                          // páginas fuera del catálogo → activas
  const cfg = _flags[grupo];
  if (!cfg) return true;                            // grupo ausente en la config → activo
  if (cfg.on === false) return false;               // grupo apagado
  return !cfg.subs || cfg.subs[pageId] !== false;   // submódulo apagado explícitamente
}

// Aplica flags recibidos por realtime sin volver a leer de la nube.
function aplicarFlags(flags) {
  _flags = (flags && typeof flags === "object" && Object.keys(flags).length) ? flags : null;
}

// Reflejo instantáneo: se suscribe a un canal de difusión del proyecto central
// (`modulos-<tenantId>`). Cuando el panel guarda cambios, emite el evento
// "actualizado" con los flags nuevos; aquí los aplicamos y avisamos para
// repintar el menú al instante, sin que el usuario recargue. Best-effort: si
// realtime no llega, los flags igual se refrescan en el próximo ingreso.
let _canalModulos = null;
export function suscribirModulosRealtime(tenantId, onCambio) {
  if (!tenantId) return null;
  if (_canalModulos) { try { panelClient().removeChannel(_canalModulos); } catch { /* */ } _canalModulos = null; }
  const ch = panelClient().channel(`modulos-${tenantId}`);
  ch.on("broadcast", { event: "actualizado" }, async (msg) => {
    const payload = msg && msg.payload;
    if (payload && payload.modulos) aplicarFlags(payload.modulos);
    else await cargarModulosHabilitados(tenantId);
    if (typeof onCambio === "function") onCambio();
  }).subscribe();
  _canalModulos = ch;
  return ch;
}
